import { api } from '@api/api';
import { CaseReportResource } from '@api/types/case/case-report/case-report.resource';
import { CreateReportRequest } from '@api/types/case/case-report/create-report.request';
import { makeError } from '@api/types/api-error';
import { UpdateReportRequest } from '@api/types/case/case-report/update-report.request';
import { PaginatedResult } from '@api/types/paginated-result';
import { RunReportResponse } from '@api/types/case/case-report/run-report.response';
import { RunReportRequest } from '@api/types/case/case-report/run-report.request';
import { SHORT_CACHE_DURATION } from '@api/cache-util';

// NOTE: Some of these endpoints were not being references for some reason.
// instead of removing them, I have commented them out for now.

const caseReportApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCaseReports: build.query<PaginatedResult<CaseReportResource>, number>({
      query: (caseId) => ({
        url: `case/${caseId}/report`,
      }),
      keepUnusedDataFor: SHORT_CACHE_DURATION,
      providesTags: (response) =>
        response
          ? [
              ...response.model.map((data) => ({
                id: data.reportId,
                type: 'CaseReport' as const,
              })),
              'CaseReport',
            ]
          : [],
    }),
    getRunReport: build.query<RunReportResponse<any>, RunReportRequest>({
      query: ({ caseId, ...params }) => ({
        url: `case/${caseId}/report/run`,
        params,
      }),
      keepUnusedDataFor: SHORT_CACHE_DURATION,
      transformResponse: RunReportResponse.fromJson,
    }),
    getCaseReport: build.query<
      CaseReportResource,
      { caseId: number; reportId: number }
    >({
      query: ({ caseId, reportId }) => ({
        url: `case/${caseId}/report/${reportId}`,
      }),
      keepUnusedDataFor: SHORT_CACHE_DURATION,
      providesTags: (arg) =>
        !!arg
          ? [
              {
                id: arg.reportId,
                type: 'CaseReport',
              },
            ]
          : [],
    }),
    createCaseReport: build.mutation<
      CaseReportResource,
      CreateReportRequest & { caseId: number }
    >({
      query: ({ caseId, ...body }) => ({
        url: `case/${caseId}/report`,
        method: 'POST',
        body,
      }),
      transformErrorResponse: (error) => makeError<CreateReportRequest>(error),
      invalidatesTags: (_, error) => (!error ? ['CaseReport'] : []),
    }),
    updateCaseReport: build.mutation<
      void,
      UpdateReportRequest & { caseId: number }
    >({
      query: ({ caseId, ...body }) => ({
        url: `case/${caseId}/report`,
        method: 'PUT',
        body,
      }),
      transformErrorResponse: (error) => makeError<UpdateReportRequest>(error),
      invalidatesTags: (_, error, arg) =>
        !error ? [{ id: arg.reportId, type: 'CaseReport' as const }] : [],
    }),
    // deleteReport: build.mutation<void, DeleteReportRequest>({
    //   query: ({ caseId, reportId }) => ({
    //     url: `case/${caseId}/report/${reportId}`,
    //     method: 'DELETE',
    //   }),
    //   transformErrorResponse: (error) => makeError<DeleteReportRequest>(error),
    //   invalidatesTags: (_, error) => (!error ? ['CaseReport'] : []),
    // }),
    // getCaseOverviewSolutions: build.query<RunReportResponse<any>, number>({
    //   query: (caseId) => ({
    //     url: `case/${caseId}/Report/Run/All/Coverage/Solution/SolutionTerm`,
    //   }),
    //   transformResponse: RunReportResponse.fromJson,
    //   providesTags: ['Case'],
    // }),
    getCaseOverviewSolutionCoverages: build.query<
      RunReportResponse<any>,
      number
    >({
      query: (caseId) => ({
        url: `case/${caseId}/Report/Run/All/Coverage/Solution/Coverage`,
      }),
      keepUnusedDataFor: SHORT_CACHE_DURATION,
      transformResponse: RunReportResponse.fromJson,
      // We provide tags for caseId here so that we can invalidate the cache when the case updates
      providesTags: (_, error, caseId) =>
        !error ? [{ id: caseId, type: 'Case' as const }] : [],
    }),
    getCaseOverviewSolutionTermCoverages: build.query<
      RunReportResponse<any>,
      number
    >({
      query: (caseId) => ({
        url: `case/${caseId}/Report/Run/All/Coverage/SolutionTerm`,
      }),
      keepUnusedDataFor: SHORT_CACHE_DURATION,
      transformResponse: RunReportResponse.fromJson,
      // We provide tags for caseId here so that we can invalidate the cache when the case updates
      providesTags: (_, error, caseId) =>
        !error ? [{ id: caseId, type: 'Case' as const }] : [],
    }),
    getCaseOverviewEvidenceStrengthCoverages: build.query<
      RunReportResponse<any>,
      number
    >({
      query: (caseId) => ({
        url: `case/${caseId}/Report/Run/All/Coverage/Evidence/Strength`,
      }),
      keepUnusedDataFor: SHORT_CACHE_DURATION,
      transformResponse: RunReportResponse.fromJson,
      // We provide tags for caseId here so that we can invalidate the cache when the case updates
      providesTags: (_, error, caseId) =>
        !error ? [{ id: caseId, type: 'Case' as const }] : [],
    }),
    getCaseOverviewThemes: build.query<RunReportResponse<any>, number>({
      query: (caseId) => ({
        url: `case/${caseId}/Report/Run/All/Coverage/theme/coverageAll`,
      }),
      keepUnusedDataFor: SHORT_CACHE_DURATION,
      transformResponse: RunReportResponse.fromJson,
      // We provide tags for caseId here so that we can invalidate the cache when the case updates
      providesTags: (_, error, caseId) =>
        !error ? [{ id: caseId, type: 'Case' as const }] : [],
    }),
    getCaseOverviewImpacts: build.query<RunReportResponse<any>, number>({
      query: (caseId) => ({
        url: `case/${caseId}/Report/Run/All/Coverage/impact/coverageAll`,
      }),
      keepUnusedDataFor: SHORT_CACHE_DURATION,
      transformResponse: RunReportResponse.fromJson,
      // We provide tags for caseId here so that we can invalidate the cache when the case updates
      providesTags: (_, error, caseId) =>
        !error ? [{ id: caseId, type: 'Case' as const }] : [],
    }),
  }),
});

export const {
  useGetCaseReportsQuery,
  useCreateCaseReportMutation,
  useGetCaseOverviewSolutionCoveragesQuery,
  useGetCaseOverviewImpactsQuery,
  useGetCaseOverviewThemesQuery,
  useUpdateCaseReportMutation,
  useGetCaseOverviewSolutionTermCoveragesQuery,
  useGetCaseOverviewEvidenceStrengthCoveragesQuery,
} = caseReportApi;

export default caseReportApi;
